import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import invoiceApi from "../../api/invoice";
import { Loader, toast } from "../../common/components/core";
import { FormFooter } from "../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { currentUserSelector } from "../../redux/slices/auth/selectors";
import { BesuClaimState } from "../../types/claim";
import { BankAccount } from "../../types/common";
import { InvoiceState } from "../../types/invoice";
import { BesuProjectState } from "../../types/project";
import { parseAmountString } from "../../utils/amount";
import { shortenUUID } from "../../utils/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../utils/errorHandling";
import ClaimContext from "../claim/ClaimContext";
import ProjectContext from "../project/ProjectContext";

import InvoiceForm, { InvoiceFormValues } from "./components/InvoiceForm";
import InvoicePreview from "./components/InvoicePreview";
import InvoiceContext from "./InvoiceContext";
import { useDeveloperBankAccountList } from "../../api/utilities";
import { PageTitle } from "../../types/pageTitle";

const EditInvoiceView = (): JSX.Element => {
    document.title = PageTitle.EditInvoiceView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const {
        data: claim,
    } = useContext(ClaimContext);
    const {
        data: {
            state: { data: invoice },
            ref: invoiceRef,
        },
        mutate: mutateInvoice,
    } = useContext(InvoiceContext);
    const currentUser = useSelector(currentUserSelector);
    const { data: bankAccounts, isValidating: isBankAccountsLoading } =
        useDeveloperBankAccountList(currentUser?.userProfile.id);
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);

    const submit = async (formValues: InvoiceFormValues) => {
        setSubmitting(true);
        try {
            const result = await invoiceApi.update(
                invoiceRef,
                formValues,
                bankAccounts ?? [],
            );
            mutateInvoice(result.stateAndRefMap.InvoiceState[0]);
            toast(`Invoice #${shortenUUID(invoice.linearId.id)} submitted`);
            history.push(
                `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
            );
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers(
                onStaleStateError(() => {
                    mutateInvoice();
                    history.push(
                        `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
                    );
                    toast("Page has been updated by another user.");
                }),
                onAuthError,
            )(e as GenericError);
        }
    };

    return (
        <PureEditInvoiceView
            project={project}
            claim={claim}
            invoice={invoice}
            bankAccountList={bankAccounts ?? []}
            isLoading={isSubmitting || isBankAccountsLoading}
            onSubmit={submit}
            onCancel={() =>
                history.push(
                    `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
                )
            }
        />
    );
};

type PureEditInvoiceViewProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    invoice: InvoiceState;
    bankAccountList: BankAccount[];
    isLoading: boolean;
    onSubmit: (values: InvoiceFormValues) => void;
    onCancel: () => void;
};

export const PureEditInvoiceView = ({
    project,
    claim,
    invoice,
    bankAccountList,
    isLoading,
    onCancel,
    onSubmit,
}: PureEditInvoiceViewProps): JSX.Element => {
    const [isPreview, setIsPreview] = useState(false);
    const [formValues, setFormValues] = useState<InvoiceFormValues>({
        bankAccount: invoice.props.bankAccountDetails.bankAccountHolderName + "-" + invoice.props.bankAccountDetails.bankAccountNumberStr,
        currency: invoice.props.bankAccountDetails.currency,
        amount: parseAmountString(invoice.props.amount).amount,
        files: invoice.props.documents,
    });

    useEffect(() => {
        if (!isLoading) {
            setIsPreview(false);
        }
    }, [isLoading]);

    const preview = (values: InvoiceFormValues) => {
        setFormValues(values);
        setIsPreview(true);
    };

    return (
        <Loader overlay isLoading={isLoading} bgClassName="bg-body">
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink
                        to={`/project/${project.referenceId}/claim/${claim.referenceId}`}
                    >
                        Claim {shortenUUID(claim.referenceId)}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    Invoice {shortenUUID(invoice.linearId.id)}
                </Breadcrumb.Item>
            </Breadcrumb>
            {isPreview ? (
                <>
                    <h1 className="mb-5">Preview</h1>
                    <InvoicePreview
                        bankAccountList={bankAccountList}
                        formValues={formValues}
                    />
                    <hr className="bg-border-grey my-3" />
                    <FormFooter className="d-flex justify-content-between">
                        <Button
                            variant="light.tertiary"
                            onClick={() => setIsPreview(false)}
                        >
                            Back
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={() => onSubmit(formValues)}
                        >
                            Submit
                        </Button>
                    </FormFooter>
                </>
            ) : (
                <InvoiceForm
                    bankAccountList={bankAccountList}
                    values={formValues}
                    onCancel={onCancel}
                    onSubmit={preview}
                />
            )}
        </Loader>
    );
};

export default EditInvoiceView;
